import { ActionCentre } from "../global/ActionCentre";
import { HotelPopup } from "../global/HotelPopup";
import { IHotelSearchResult, IHotelSearchResultJSONOfferDetails, IStarRatingInfo, IXMLHttpConfig } from "../global/types";
import { XMLHttpConfig } from "../global/XMLHttpConfig";
import { HttpService } from "../services/HttpService";

declare var _gaq: any;

export class HotelSearch {
    private actionsService: ActionCentre;
    private httpSvc: HttpService;
    private nodeId: number;
    private hotelCountObject: IStarRatingInfo[] = [];
    private activeHotelCountIndex: number;
    private isLoadingNow: boolean = false;
    private useInfinityScroll: boolean = false;
    private numberOfHotelsToLoad: number = 9;

    constructor(actionsService: ActionCentre) {
        this.actionsService = actionsService;
        this.httpSvc = new HttpService();
        this.getLocationNodeId();
        this.getLocationHotelCounts();
        this.getInfinitizeState();
        this.bindInteractions();
    }

    // Initialization.

    private getLocationNodeId() {
        const nodeIdEl: HTMLElement = document.querySelector("[data-page-nodeid]") as HTMLElement;
        this.nodeId = parseInt(nodeIdEl.getAttribute("data-page-nodeid"), 10);
    }

    private async getLocationHotelCounts() {
        const filter: HTMLElement = document.querySelector("[data-filter-stars]") as HTMLElement;
        
        if (filter) {
            const url: string = `/utility/getHotelsByStarRating2018.aspx?summary=true&weblocationnodeid=${this.nodeId}`;
            const response: any = await this.httpSvc.get(new XMLHttpConfig(url));
            this.createHotelCountObjectAndGetInitialHotels(response);
        }
    }

    private getInfinitizeState() {
        const infinitizeStateEl: HTMLElement = document.querySelector("[data-page-use-infinity-scroll]") as HTMLElement;
        this.useInfinityScroll = (infinitizeStateEl.getAttribute("data-page-use-infinity-scroll") === "true") ? true : false;
    }

    private createHotelCountObjectAndGetInitialHotels(response: any) {
        for (let i = 0; i < response.length; i++) {
            const starRatingObject: IStarRatingInfo = {
                starRating: response[i].StarRating,
                hotelCount: response[i].HotelCount,
                hotelsShowing: 0,
            };

            this.hotelCountObject.push(starRatingObject);
        }

        this.createStarRatingFilter();
        this.getInitialHotels();
    }

    private createStarRatingFilter() {
        const filter: HTMLElement = document.querySelector("[data-filter-stars]") as HTMLElement;

        for (let i = 5; i > 0; i--) {
            const filterItem: HTMLElement = document.createElement("span");
            filterItem.classList.add("filter-star");

            filterItem.setAttribute("data-star-filter-style", (i).toString());

            if (this.haveHotelsOfStarRating(i)) {
                filterItem.setAttribute("data-star-filter", (i).toString());
            }

            filter.appendChild(filterItem);
        }

        this.bindStarFilterBehaviour();
    }

    private bindStarFilterBehaviour() {
        const allStars: NodeListOf<HTMLElement> = document.querySelectorAll("[data-star-filter-style]") as NodeListOf<HTMLElement>;

        for (let i = 0; i < allStars.length; i++) {
            if (allStars[i].hasAttribute("data-star-filter")) {
                allStars[i].addEventListener("click", () => {
                    const starRating: number = parseInt(allStars[i].getAttribute("data-star-filter"), 10);
                    this.changeStarRatingTo(starRating);
                    _gaq.push(["_trackEvent", "Interaction", "Hotels - filter by star rating VIA TOP FILTER", document.URL, 0, false]);
                });

                allStars[i].addEventListener("mouseover", () => {
                    allStars[i].classList.add("hoveredon");
                    for (let j = 4; j > 0; j--) {
                        if (j >= i) {
                            allStars[j].classList.add("solid");
                        }
                    }
                });

                allStars[i].addEventListener("mouseout", () => {
                    allStars[i].classList.remove("hoveredon");
                    for (let j = 4; j > 0; j--) {
                        allStars[j].classList.remove("solid");
                    }
                });
            }
        }
    }

    private async getInitialHotels() {
        const firstLoad: number = this.getFirstLoadParameter();
        this.isLoadingNow = true;
        const url: string = this.getUrlForStarRating(firstLoad);
        const response: IHotelSearchResult[] = await this.httpSvc.get(new XMLHttpConfig(url));
        this.handleHotelResponse(response);
    }

    private bindInteractions() {
        const viewMoreBtn: HTMLElement = document.querySelector("[data-more-hotels-button-more]") as HTMLElement;
        const viewAllBtn: HTMLElement = document.querySelector("[data-more-hotels-button-all]") as HTMLElement;
        const starBtns: NodeListOf<HTMLElement> = document.querySelectorAll("[data-star-bottom]") as NodeListOf<HTMLElement>;

        if (viewMoreBtn) {
            viewMoreBtn.addEventListener("click", () => {
                this.viewMore();
            });
        }
        
        if (viewAllBtn) {
            viewAllBtn.addEventListener("click", () => {
                this.viewMore(true);
            });
        }

        if (this.useInfinityScroll) {
            window.addEventListener("DOMContentLoaded", () => { this.infinityScroll(); }, false);
            window.addEventListener("load", () => { this.infinityScroll(); }, false);
            window.addEventListener("scroll", () => { this.infinityScroll(); }, false);
            window.addEventListener("resize", () => { this.infinityScroll(); }, false); 
        }
        
        for (let i = 0; i < starBtns.length; i++) {
            starBtns[i].addEventListener("click", (e) => {
                e.preventDefault();
                e.stopPropagation();
                this.scrollToTop(300);
                const body: HTMLElement = document.querySelector("body") as HTMLElement;
                const rating: number = parseInt(starBtns[i].getAttribute("data-star-bottom"), 10);

                setTimeout(() => {
                    this.changeStarRatingTo(rating);
                }, 300);

                _gaq.push(["_trackEvent", "Interaction", "Hotels - filter by star rating VIA BOTTOM FILTER", document.URL, 0, false]);
            });
        }
    }

    // Utility.

    private scrollToTop(scrollDuration) {
        const scrollStep: number = -window.scrollY / (scrollDuration / 15);
        const scrollInterval = setInterval(() => {
            if (window.scrollY !== 0) {
                window.scrollBy(0, scrollStep);
            } else { clearInterval(scrollInterval); }
        }, 15);
    }

    private getFirstLoadParameter(): number {
        const hash = location.hash;

        if (hash.indexOf("#5star") > -1) {
            return 5;
        }

        if (hash.indexOf("#4star") > -1) {
            return 4;
        }

        if (hash.indexOf("#3star") > -1) {
            return 3;
        }

        return this.hotelCountObject[0].starRating;
    }

    private setHashFor(stars: number) {
        switch (stars) {
            case 5:
                location.hash = "#5star";
                return;
            case 4:
                location.hash = "#4star";
                return;
            case 3:
                location.hash = "#3star";
                return;
            // CMG 05/03/18: Instructed by Capt Pearce to only display 3* hotels and above
            // case 2:
            //    location.hash = "#2star";
            //    return;
        }

        location.hash = `#${this.hotelCountObject[0].starRating}star`;
    }

    private getUrlForStarRating(rating: number, all?: boolean): string {
        let ratingObj: IStarRatingInfo;

        for (let i = 0; i < this.hotelCountObject.length; i++) {
            if (rating === this.hotelCountObject[i].starRating) {
                this.activeHotelCountIndex = i;
                ratingObj = this.hotelCountObject[i];
            } else {
                this.hotelCountObject[i].hotelsShowing = 0;
            }
        }

        const urlBase: string = `/utility/getHotelsByStarRating2018.aspx`;
        const starRating: string = `starRating=${ratingObj.starRating}`;
        let numOfHotels: string = `numhotelstodisplay=${this.numberOfHotelsToLoad}`;
        const webLocId: string = `weblocationnodeid=${this.nodeId}`;
        const displayedH: string = `displayedhotels=${ratingObj.hotelsShowing}`;
        const availablehotels: string = `availablehotels=${ratingObj.hotelCount}`;

        if (all) {
            numOfHotels = `numhotelstodisplay=${this.numberOfHotelsToLoad}`; // `numhotelstodisplay=${ratingObj.hotelCount}`;
        }

        this.setHashFor(ratingObj.starRating);

        return `${urlBase}?${starRating}&${numOfHotels}&${webLocId}&${displayedH}&${availablehotels}`;
    }

    private haveHotelsOfStarRating(n: number): boolean {
        let result: boolean = false;
        
        for (let i = 0; i < this.hotelCountObject.length; i++) {
            if (n > 2 && this.hotelCountObject[i].starRating === n) {
                result = true;
            }
        }

        return result;
    }

    private createHotelHTML(hotel: IHotelSearchResult): HTMLElement {
        let hotelHTML: HTMLElement;

        if (hotel.HasSpecialOffer) {
            hotelHTML = document.createElement("div");
        } else {
            hotelHTML = document.createElement("a");
        }

        const img = hotel.ImageURL;
        const name = hotel.Name;
        const stars = hotel.StarRating;
        const short = hotel.ChoppedDescription;
        const price = hotel.Price;
        const offerText = hotel.OfferText;

        const itemBG: HTMLElement = document.createElement("div");
        itemBG.classList.add("hs__stage__item__img");
        itemBG.style.backgroundImage = `url('${img}')`;

        const heading: HTMLElement = document.createElement("h5");
        heading.innerText = name;

        const starRating: HTMLElement = document.createElement("h6");
        starRating.classList.add("pt-single");
        starRating.classList.add("pb-single");
        starRating.innerHTML = stars;

        const description: HTMLElement = document.createElement("p");
        description.classList.add("pt-single");
        description.classList.add("pb-single");
        description.innerText = `${short}...`;

        const priceEl: HTMLElement = document.createElement("p");
        priceEl.innerHTML = `Fr <strong>£<span>${price}</span></strong> per room night`;
        priceEl.classList.add("pb-single");
        priceEl.classList.add(`value-${price}`);

        const offerTextEl: HTMLElement = document.createElement("p");
        offerTextEl.classList.add("daf-red");
        offerTextEl.innerHTML = offerText;

        hotelHTML.classList.add("hs__stage__item");
        hotelHTML.classList.add("transition");
        hotelHTML.classList.add("opacity0");
        hotelHTML.setAttribute("href", hotel.HotelURL);
        hotelHTML.setAttribute("data-hotel-id", hotel.Id.toString());
        hotelHTML.appendChild(itemBG);
        hotelHTML.appendChild(heading);
        hotelHTML.appendChild(starRating);
        hotelHTML.appendChild(description);
        hotelHTML.appendChild(priceEl);
        hotelHTML.appendChild(offerTextEl);
   
        if (hotel.HasSpecialOffer) {
            const offerPopup: HotelPopup = new HotelPopup(this.actionsService, hotel.OfferDetails);
            hotelHTML.classList.add("cursor");

            hotelHTML.addEventListener("click", (e) => {
                _gaq.push(["_trackEvent", "Interaction", "Hotels - view hotel with special offer", document.URL, 0, false]);
                e.stopPropagation();
                offerPopup.ActivatePopup();
            });
        }

        return hotelHTML;
    }

    private clearStage() {
        const stage: HTMLElement = document.querySelector("[data-hotel-search-stage]") as HTMLElement;
        stage.innerHTML = "";
    }

    // Handle fetched data.

    private handleHotelResponse(response: IHotelSearchResult[]) {
        const hotelSearchStage: HTMLElement = document.querySelector("[data-hotel-search-stage]") as HTMLElement;

        let hotelHTML: HTMLElement;
        let counter: number = 0;

        if (response.length > 0) {
            for (let i = 0; i < response.length; i++) {
                hotelHTML = this.createHotelHTML(response[i]);
                hotelSearchStage.appendChild(hotelHTML);
                counter++;
            }

            const hotels: NodeListOf<HTMLElement> = document.querySelectorAll(".hs__stage__item.opacity0") as NodeListOf<HTMLElement>;

            for (let j = 0; j < hotels.length; j++) {
                setTimeout(() => {
                    hotels[j].classList.remove("opacity0");
                }, 50 * j);
            }
            // only set isLoadingNow to false if this latest xhr call returns results
            this.isLoadingNow = false;
        }

        // Update visible hotel count.        
        this.toggleViewMoreButton(true);
        this.toggleStarRatingEnabled(true);
        this.toggleTopHeaderIndicator(true);
        this.toggleStageLoading(false);
        this.updateVisibleHotelCountAndLeftToDisplay(counter);
        this.updateStarRatingIndicators();
    }

    // Indicator updates.

    private updateVisibleHotelCountAndLeftToDisplay(counterValue: number) {
        const btn: HTMLElement = document.querySelector("[data-more-hotels-button]") as HTMLElement;
        const hotelsInStarRatingEl: NodeListOf<HTMLElement> = document.querySelectorAll("[data-stars-displaying-count]") as NodeListOf<HTMLElement>;
        // const leftToDisplayEl: HTMLElement = document.querySelector("[data-stars-remaining]") as HTMLElement;
        const totalCount: number = this.hotelCountObject[this.activeHotelCountIndex].hotelCount;
        const hotelsShowing: number = document.querySelectorAll("[data-hotel-id]").length;
        let leftToDisplay: number;

        this.hotelCountObject[this.activeHotelCountIndex].hotelsShowing += counterValue;
        leftToDisplay = totalCount - hotelsShowing;

        for (let i = 0; i < hotelsInStarRatingEl.length; i++) {
            hotelsInStarRatingEl[i].innerText = this.hotelCountObject[this.activeHotelCountIndex].hotelCount.toString();
        }

        if (leftToDisplay > 0) {
            this.toggleViewMoreButton(true);
            // leftToDisplayEl.innerText = leftToDisplay.toString();
        } else {
            this.toggleViewMoreButton(false);
        }
    }

    private updateStarRatingIndicators() {
        const indicators: NodeListOf<HTMLElement> = document.querySelectorAll("[data-stars-displaying]") as NodeListOf<HTMLElement>;
        const mainRating: HTMLElement = document.querySelector("[data-filter-stars]") as HTMLElement;

        for (let i = 0; i < indicators.length; i++) {
            indicators[i].innerText = this.hotelCountObject[this.activeHotelCountIndex].starRating.toString();
        }

        mainRating.setAttribute("data-filter-stars", this.hotelCountObject[this.activeHotelCountIndex].starRating.toString());
    }

    private toggleStarRatingEnabled(status: boolean) {
        const starBox: HTMLElement = document.querySelector("[data-star-rating-box]") as HTMLElement;

        if (status) {
            starBox.classList.remove("disable");
        } else {
            starBox.classList.add("disable");
        }
    }

    private toggleViewMoreButton(status: boolean) {
        const button: HTMLElement = document.querySelector("[data-more-hotels-button]") as HTMLElement;
        button.style.display = status ? "block" : "none";   
    }

    private toggleTopHeaderIndicator(status: boolean) {
        const header: HTMLElement = document.querySelector("[data-indicator-container]") as HTMLElement;
        // header.style.display = status ? "block" : "none";  

        setTimeout(() => {
            if (status) {
                header.classList.add("opacity1");
                header.classList.add("leftSlideActive");
            } else {
                header.classList.remove("opacity1");
                header.classList.remove("leftSlideActive");
            }
        }, 100);        
    }

    private toggleStageLoading(status: boolean) {       
        const loader: HTMLElement = document.querySelector("[data-stage-loading]") as HTMLElement;
        loader.style.display = status ? "block" : "none";  
        setTimeout(() => {
            loader.classList.toggle("active");
        }, 100);
    }

    // Functionality.

    private async viewMore(all?: boolean) {
        let url: string;

        if (this.hotelCountObject[this.activeHotelCountIndex]) {
            if (all) {
                url = this.getUrlForStarRating(this.hotelCountObject[this.activeHotelCountIndex].starRating, true);
            } else {
                url = this.getUrlForStarRating(this.hotelCountObject[this.activeHotelCountIndex].starRating);
            }

            this.isLoadingNow = true;  
            this.toggleStageLoading(true);
            this.toggleStarRatingEnabled(false);
            this.toggleViewMoreButton(false);          
            const response: IHotelSearchResult[] = await this.httpSvc.get(new XMLHttpConfig(url));
            // only set isLoadingNow to false if this latest xhr call returns results
            this.isLoadingNow = (response.length > 0) ? false : true;  
            this.handleHotelResponse(response);                               
        }        
    }

    private changeStarRatingTo(n: number) {
        // if (!this.isLoadingNow) {
            const url = this.getUrlForStarRating(n);

            if (this.hotelCountObject[this.activeHotelCountIndex].hotelsShowing === 0) {
                this.toggleTopHeaderIndicator(false);
                this.clearStage();
                this.viewMore();
            }
        // }
    }

    private infinityScroll() {
    
        if (this.isElementInViewport(document.querySelector("[data-infinity-scroll-in-view]") as HTMLElement) && !this.isLoadingNow) {
            this.isLoadingNow = true;
            this.viewMore();
            _gaq.push(["_trackEvent", "Interaction", "Hotels - view more triggered with scroll", document.URL, 0, false]);
        }

    }

    private isElementInViewport(el: HTMLElement): boolean {

        if (el) {
            const rect: ClientRect = el.getBoundingClientRect();

            return (
                rect.top >= 0 &&
                rect.left >= 0 &&
                rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
                rect.right <= (window.innerWidth || document.documentElement.clientWidth)
            );
        }
        return false;
    }
}



// WEBPACK FOOTER //
// ./src/hotels/HotelSearch.ts